"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "~/trpc/react";

export interface SessionStatus {
  isExpired: boolean;
  isExpiringSoon: boolean;
  timeRemaining: number;
  isLoading: boolean;
}

export function useSessionStatus(options: {
  warningThresholdMinutes?: number;
  checkIntervalSeconds?: number;
  autoLogoutOnExpiry?: boolean;
} = {}): SessionStatus {
  const {
    warningThresholdMinutes = 5,
    checkIntervalSeconds = 60,
    autoLogoutOnExpiry = true,
  } = options;

  const router = useRouter();
  const [status, setStatus] = useState<SessionStatus>({
    isExpired: false,
    isExpiringSoon: false,
    timeRemaining: 0,
    isLoading: true,
  });

  const { data: session, isLoading } = api.auth.getSession.useQuery(
    undefined,
    {
      refetchInterval: checkIntervalSeconds * 1000,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    if (isLoading) {
      setStatus((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    if (!session?.user?.accessTokenExpiresAt) {
      setStatus({
        isExpired: true,
        isExpiringSoon: false,
        timeRemaining: 0,
        isLoading: false,
      });

      if (autoLogoutOnExpiry) {
        router.push(`/login?session=expired&callbackUrl=${window.location.pathname}`);
      }
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.user.accessTokenExpiresAt;
    const timeRemaining = expiresAt - now;

    const isExpired = timeRemaining <= 0;
    const isExpiringSoon = timeRemaining <= warningThresholdMinutes * 60 && timeRemaining > 0;

    setStatus({
      isExpired,
      isExpiringSoon,
      timeRemaining: Math.max(0, timeRemaining),
      isLoading: false,
    });

    if (isExpired && autoLogoutOnExpiry) {
      router.push(`/login?session=expired&callbackUrl=${window.location.pathname}`);
    }
  }, [session, isLoading, warningThresholdMinutes, autoLogoutOnExpiry, router]);

  return status;
}

